// src/pages/achete-terrain/Sites.jsx
import React, { useEffect, useState } from 'react';
import {
  Table, Input, Button, Breadcrumb, Modal, message,
  DatePicker, Space, Grid, Select, Tag
} from 'antd';
import {
  EyeOutlined, DeleteOutlined, ReloadOutlined,
  FileExcelOutlined, FilePdfOutlined, DownloadOutlined,
  SearchOutlined, PlusOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { acheteTerrainService } from '../../common/services';
import dayjs from 'dayjs';


const { RangePicker } = DatePicker;
const { Option } = Select;

const Sites = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // recherche / filtres
  const [q, setQ] = useState('');
  const [viabilise, setViabilise] = useState(undefined); // 'true' | 'false' | undefined
  const [dateRange, setDateRange] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';
  const btnFull = { width: isMobile ? '100%' : 'auto' };

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        q: q || undefined,
        viabilise, // 'true' | 'false' | undefined (ton contrôleur le gère)
        // si tu ajoutes ces filtres côté API plus tard :
        // start: dateRange?.[0]?.format('YYYY-MM-DD'),
        // end: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const data = await acheteTerrainService.listSites(params);

      // compat: le contrôleur renvoie { items, total, page, limit }
      setItems(Array.isArray(data) ? data : (data.items || []));
      setTotal(Array.isArray(data) ? data.length : (data.total || 0));
    } catch (e) {
      console.error('Erreur chargement sites :', e);
      message.error("Impossible de charger les sites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, q, viabilise, dateRange]);

  const handleDelete = (site) => {
    Modal.confirm({
      title: "Supprimer le site ?",
      content: (
        <>
          <p><strong>Nom :</strong> {site.nom}</p>
          <p><strong>Localisation :</strong> {site.localisation}</p>
          <p>Si des lots sont <em>vendus</em>, utilisez la suppression forcée.</p>
        </>
      ),
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      onOk: async () => {
        try {
          await acheteTerrainService.deleteSite(site._id); // normal
          message.success("Site supprimé.");
          fetchList();
        } catch (e) {
          // si 409 (lots vendus), proposer force=true
          const needForce = e?.status === 409 || String(e?.message || '').includes('vendu');
          if (needForce) {
            Modal.confirm({
              title: "Suppression forcée ?",
              content: "Ce site contient des lots vendus. Confirmez la suppression forcée.",
              okText: "Forcer",
              okType: "danger",
              cancelText: "Annuler",
              centered: true,
              onOk: async () => {
                try {
                  await acheteTerrainService.deleteSite(site._id, { force: true });
                  message.success("Site supprimé (forcé).");
                  fetchList();
                } catch (err2) {
                  console.error(err2);
                  message.error("Échec de la suppression forcée.");
                }
              }
            });
          } else {
            console.error(e);
            message.error("Échec de la suppression du site.");
          }
        }
      }
    });
  };

  const handleRecompute = async (site) => {
    try {
      await acheteTerrainService.recomputeNombreLots(site._id);
      message.success("nombreTotalLots recalculé.");
      fetchList();
    } catch (e) {
      console.error(e);
      message.error("Échec du recalcul.");
    }
  };

  // Exports rapides (page courante)
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(items);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sites");
    XLSX.writeFile(workbook, "sites.xlsx");
  };

  const exportToCSV = () => {
    const worksheet = XLSX.utils.json_to_sheet(items);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'sites.csv');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Liste des sites", 14, 10);
    autoTable(doc, {
      startY: 20,
      head: [['Nom', 'Localisation', 'Viabilisé', 'Lots (total)', 'Créé le']],
      body: items.map((s) => [
        s.nom,
        s.localisation,
        s.estViabilise ? 'Oui' : 'Non',
        s.nombreTotalLots ?? (s.lots?.length || 0),
        s.createdAt || '',
      ]),
    });
    doc.save("sites.pdf");
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
    },
    {
      title: 'Localisation',
      dataIndex: 'localisation',
      key: 'localisation',
      responsive: ['md'],
    },
    {
      title: 'Viabilisé',
      dataIndex: 'estViabilise',
      key: 'estViabilise',
      render: (v) => v ? <Tag color="green">Oui</Tag> : <Tag>Non</Tag>,
      width: 120,
    },
    {
      title: 'Nombre de lots',
      key: 'nombreTotalLots',
      render: (_, s) => s?.nombreTotalLots ?? (s?.lots?.length || 0),
      width: 140,
    },
   {
  title: 'Créé le',
  dataIndex: 'createdAt',
  key: 'createdAt',
  responsive: ['lg'],
  render: (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—',
},

    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space wrap>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/dashboard/site/${record._id}`)}
          />
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={() => handleRecompute(record)}
            title="Recalculer le nombreTotalLots"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const onTableChange = (pagination /*, _filters, sorter */) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
    // NOTE: ton contrôleur ne gère pas (encore) le tri serveur ⇒ on ignore sorter
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row">
          <div className="col-md-12">

            <Breadcrumb
              style={{ marginBottom: 16 }}
              items={[
                { title: <Link to="/dashboard" style={{ fontWeight: 'bold' }}>🏠 ACCUEIL</Link> },
                { title: <span style={{ fontWeight: 'bold' }}>🧱 SITES</span> },
              ]}
            />

            <div className="card mb-4">
              <div className="card-body">

                {/* Filtres */}
                <div className="d-flex justify-content-between mb-3 flex-wrap gap-8">
                  <Space wrap>
                    <Input
                      prefix={<SearchOutlined />}
                      placeholder="Rechercher nom / localisation"
                      value={q}
                      onChange={(e) => { setQ(e.target.value); setPage(1); }}
                      allowClear
                      style={{ width: 280 }}
                      size={controlSize}
                    />

                    <Select
                      allowClear
                      placeholder="Filtrer: viabilisé ?"
                      value={viabilise}
                      onChange={(v) => { setViabilise(v); setPage(1); }}
                      style={{ width: 180 }}
                      size={controlSize}
                    >
                      <Option value="true">Oui</Option>
                      <Option value="false">Non</Option>
                    </Select>

                    {/* Si tu ajoutes le filtre de dates côté API, dé-commente :
                    <RangePicker
                      onChange={(r) => { setDateRange(r); setPage(1); }}
                      placeholder={['Date début', 'Date fin']}
                      format="DD/MM/YYYY"
                      allowClear
                      size={controlSize}
                    />
                    */}
                  </Space>

                  <Space wrap>
                    <Button icon={<PlusOutlined />} type="primary" size={controlSize}
                      onClick={() => navigate('/dashboard/ajout-site')}
                    >
                      Nouveau site
                    </Button>

                    <Button icon={<FileExcelOutlined />} onClick={exportToExcel} size={controlSize} style={btnFull}>
                      Excel
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={exportToCSV} size={controlSize} style={btnFull}>
                      CSV
                    </Button>
                    <Button icon={<FilePdfOutlined />} onClick={exportToPDF} danger size={controlSize} style={btnFull}>
                      PDF
                    </Button>
                  </Space>
                </div>

                {/* Table */}
                <Table
                  rowKey="_id"
                  columns={columns}
                  dataSource={items}
                  loading={isLoading}
                  onChange={onTableChange}
                  pagination={{
                    current: page,
                    pageSize: limit,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [5, 10, 20, 50, 100],
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sites;
